// The project's REAL talent outreach, parsed out of the fat admin payload.
//
// `ExplorerProjectDetail.sellerThreads` arrives as `to_wire()` dicts (api.ts
// explains why the legacy admin routes are hand-mirrored rather than generated),
// so everything here parses DEFENSIVELY: a key the backend renames lands as a
// missing field that renders as "unknown", never a throw mid-render.
//
// WHAT THIS IS NOT: a metric source. Talent Management's rates, averages and
// benchmarks are BI-owned (admin/CLAUDE.md) and come from curated BigQuery. The
// columns built here mirror that dashboard's ROW SHAPE, but every value is a
// fact of ONE record: this talent's tier, this thread's reply latency, this
// thread's offer. No rate, no average, no cross-talent aggregate is computed in
// this file, and none may be added — that is the line between showing a live
// operational record and quietly forking the metric layer. It is also why there
// is no Willingness column here: three rival formulas define it and BI owns
// which one is real.
import { minutesBetween } from "./format";
import { projectTitle } from "./project-title";

// --- tolerant readers --------------------------------------------------------

function dict(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

/** String items only. A bullet list that arrived carrying a nested object drops
 *  that entry rather than rendering "[object Object]" at an operator. */
function strings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string" && v.trim() !== "");
}

// --- the shapes --------------------------------------------------------------

/** One extracted proposal. `custom_offers`, as JUNO scored it. */
export interface SellerOffer {
  id: string;
  /** The LOW end when the talent quoted a RANGE, the single figure otherwise. */
  priceCents: number | null;
  /** The HIGH end of a quoted RANGE; null for a single figure (and for every
   *  offer written before migration 0157). */
  priceMaxCents: number | null;
  currency: string | null;
  timelineDays: number | null;
  /** NULL when the talent never stated a number. Never back-filled: a `-1`
   *  sentinel used to leak into this field, so anything negative reads as
   *  unstated rather than as a count. */
  revisionRounds: number | null;
  scopeBullets: string[];
  inclusions: string[];
  exclusions: string[];
  assumptions: string[];
  risksFlagged: string[];
  fitScore: number | null;
  completenessScore: number | null;
  receivedAt: string | null;
  expiresAt: string | null;
  /** JUNO's pick for this run. */
  recommended: boolean;
}

export type MessageDirection = "to_seller" | "from_seller" | "unknown";

/** Who wrote it. STERLING signs OUTWARD as "Mira", but this is the employee
 *  console: an operator auditing a thread needs the real author, and the
 *  client-voiced deal-push carve-out is a genuinely different speaker. */
export type MessageAuthor = "sterling" | "client" | "talent" | "unknown";

export interface SellerMessage {
  id: string;
  direction: MessageDirection;
  author: MessageAuthor;
  body: string;
  sentAt: string | null;
  /** A never-delivered outreach DRAFT (`metadata.pending_approval`), persisted
   *  for client review before approval. The flag is never cleared, and every
   *  talent-facing read filters these out — so an admin view that showed one
   *  unlabelled would be claiming we sent something we did not. */
  draft: boolean;
  /** The talent's inbox auto-reply or AI Personal Assistant
   *  (`metadata.automated`), not the human. The Talent dashboard carries the
   *  same caveat on its response counts. */
  automated: boolean;
}

/** One talent approached, flattened from run → thread so the tab can rank every
 *  talent in the project against every other. */
export interface SellerThreadRow {
  /** `seller_threads.id` — the row key and the selection id. */
  id: string;
  runId: string;
  runStatus: string | null;
  runStartedAt: string | null;
  sellerId: string;
  /** The talent's own display name where SCOUT captured one, else the id (which
   *  IS their Fiverr username). */
  displayName: string;
  headline: string | null;
  profileUrl: string | null;
  /** This buyer⇄talent conversation in Fiverr OS Chats Research: what the
   *  MARKETPLACE recorded, beside what Mira persisted. Built backend-side
   *  (`recruiter/fiverr_os.py`) so it points at the right Fiverr OS for this
   *  environment, and null whenever either side has no numeric Fiverr id (a
   *  guest client, or a talent we captured no id for). Null means NO LINK: an
   *  operator is told nothing rather than sent somewhere that cannot answer. */
  fiverrOsChatUrl: string | null;
  avatarUrl: string | null;
  state: string;
  declineReason: string | null;
  declineDetail: string | null;
  pickRationale: string | null;
  pickDomainRelevance: string | null;

  // The Talent dashboard's identity dimensions, read off THIS thread's own
  // profile snapshot (`seller_profile_json.dim`, migration 0071) — the same
  // keys SCOUT files as dimensional observations, so the two surfaces name a
  // talent the same way without this one querying the warehouse.
  searchTier: string | null;
  country: string | null;
  sellerLevel: string | null;
  subCategoryId: number | null;
  rating: number | null;
  completedOrders: number | null;
  isPro: boolean | null;

  // Per-thread facts. Single records, never aggregates (see the module header).
  approachedAt: string | null;
  firstReplyAt: string | null;
  lastMessageAt: string | null;
  /** Approach → the talent's first reply, in minutes. */
  repliedInMinutes: number | null;
  /** Approach → their FIRST proposal, in minutes. */
  offerInMinutes: number | null;
  /** DELIVERED messages. Drafts are excluded: they were never sent. */
  messageCount: number;
  messages: SellerMessage[];
  offers: SellerOffer[];
  /** The most recent proposal — what the row's Offer column prints. A thread
   *  gets a second one when terms are renegotiated. */
  latestOffer: SellerOffer | null;
  /** This talent carries the run's recommended offer. */
  recommended: boolean;
  /** The CLIENT's own act on this talent: a `buyer_seller_handoffs` row for the
   *  project (they pressed the card, or contacted them). Folded in by the
   *  backend from the SAME set the SCOUT shortlist beneath this table badges
   *  off, so the two blocks cannot disagree. Tri-state: `null` is UNKNOWN (the
   *  talent page's cross-project read resolves no pick set, and an older
   *  backend sends no field), never "not picked". */
  picked: boolean | null;
}

// --- status ------------------------------------------------------------------

export type StatusTone = "success" | "warning" | "danger" | "neutral";

export interface ThreadStatus {
  label: string;
  tone: StatusTone;
}

/** `seller_threads.state` → what an operator reads. An OPEN vocabulary: the
 *  CHECK constraint owns the real set, so an unrecognized state degrades to its
 *  own de-underscored text rather than being forced into a bucket. */
const STATUS: Record<string, ThreadStatus> = {
  queued: { label: "Queued", tone: "neutral" },
  sent: { label: "Approached", tone: "neutral" },
  awaiting_seller: { label: "Awaiting talent", tone: "neutral" },
  awaiting_buyer: { label: "Awaiting client", tone: "warning" },
  offer_received: { label: "Offer received", tone: "success" },
  declined: { label: "Declined", tone: "danger" },
  timed_out: { label: "Timed out", tone: "warning" },
};

/** JUNO's pass mark, mirroring `concierge/tools.py::OFFER_VALID_FIT_MIN`. An
 *  offer at or above it is a finalist candidate; below it the product will not
 *  show the client the offer at all, so "rejected" is the literal consequence,
 *  not an interpretation.
 *
 *  Mirrored rather than shared because these threads arrive as hand-mirrored
 *  `to_wire()` dicts, not generated types (see the module header) — there is no
 *  generated constant to import. If the backend moves the bar, move it here. */
export const OFFER_VALID_FIT_MIN = 60;

/** Did JUNO pass this offer? `null` means UNJUDGED, not failed: the score is
 *  absent only when the field never arrived (a rename, an older payload), and
 *  calling that "rejected" would invent a verdict nobody reached. */
export function offerAccepted(offer: SellerOffer | null | undefined): boolean | null {
  const score = offer?.fitScore;
  if (typeof score !== "number") return null;
  return score >= OFFER_VALID_FIT_MIN;
}

/** The two states that mean "nobody has answered yet". A thread carrying a
 *  judged offer is never one of these again — see `threadStatus`. */
const WAITING_STATES = new Set(["awaiting_seller", "awaiting_buyer"]);

/**
 * What an operator reads for one thread.
 *
 * `offer_received` is the one state that is not the whole story: it says the
 * talent quoted, and says nothing about whether the quote SURVIVED. JUNO judges
 * every extracted offer (timeline + scope, a binary PASS/FAIL landing in
 * `custom_offers.fit_score`) and a failing one is filtered out of the finalists
 * — the client never sees it. So a thread whose offer JUNO rejected looked
 * identical, in green, to one whose offer became a finalist card.
 *
 * A REACHED VERDICT DOES NOT UN-REACH ITSELF, so it also outranks the two
 * waiting states. `seller_threads.state` is carrying two different jobs — an
 * outcome (`offer_received` / `declined` / `timed_out`) and a workflow position
 * (who owes the next message) — and the position keeps overwriting the outcome:
 * once a talent has quoted, ANY further turn on that thread moves the state on.
 * A plain follow-up ("thanks, when do we start?") lands in the seller-turn
 * handler's fallback branch, which writes STERLING's default `awaiting_seller`;
 * an escalation writes `awaiting_buyer`. Both wipe `offer_received` off a thread
 * whose offer is still live and still on a finalist card, and the badge used to
 * follow them backwards into "Awaiting talent". Reading the OFFER rather than
 * the state is what makes the verdict stick.
 *
 * Terminal states still win, because they are outcomes too and they are later
 * ones: a talent who quotes and then withdraws reads `Declined`, not `Offer
 * accepted`. Ordering matters — check the verdict before the state map, and only
 * for states that are not themselves an ending.
 *
 * Pass the thread's latest offer. Called without one the function is the plain
 * state map it always was, so a caller with no offer in hand (or an offer that
 * arrived unscored) reads the state, never a verdict nobody reached.
 */
export function threadStatus(state: string, offer?: SellerOffer | null): ThreadStatus {
  if (state === "offer_received" || WAITING_STATES.has(state)) {
    const accepted = offerAccepted(offer);
    if (accepted === true) return { label: "Offer accepted", tone: "success" };
    if (accepted === false) return { label: "Offer rejected", tone: "danger" };
  }
  return STATUS[state] ?? { label: state.replaceAll("_", " ") || "Unknown", tone: "neutral" };
}

// --- parsing -----------------------------------------------------------------

function parseOffer(raw: unknown, recommendedOfferId: string | null): SellerOffer {
  const r = dict(raw);
  const id = str(r["id"]) ?? "";
  const rounds = num(r["revisionRounds"]);
  return {
    id,
    priceCents: num(r["priceCents"]),
    priceMaxCents: num(r["priceMaxCents"]),
    currency: str(r["currency"]),
    timelineDays: num(r["timelineDays"]),
    // A negative value is the old unstated-rounds sentinel, not a count.
    revisionRounds: rounds !== null && rounds >= 0 ? rounds : null,
    scopeBullets: strings(r["scopeBullets"]),
    inclusions: strings(r["inclusions"]),
    exclusions: strings(r["exclusions"]),
    assumptions: strings(r["assumptions"]),
    risksFlagged: strings(r["risksFlagged"]),
    fitScore: num(r["fitScore"]),
    completenessScore: num(r["completenessScore"]),
    receivedAt: str(r["receivedAt"]),
    expiresAt: str(r["expiresAt"]),
    recommended: id !== "" && id === recommendedOfferId,
  };
}

function parseDirection(value: unknown): MessageDirection {
  if (value === "to_seller" || value === "from_seller") return value;
  return "unknown";
}

function parseAuthor(sentBy: unknown): MessageAuthor {
  if (sentBy === "concierge") return "sterling";
  if (sentBy === "buyer") return "client";
  if (sentBy === "seller") return "talent";
  return "unknown";
}

function parseMessage(raw: unknown, index: number): SellerMessage {
  const r = dict(raw);
  const metadata = dict(r["metadata"]);
  return {
    id: str(r["id"]) ?? `message-${index}`,
    direction: parseDirection(r["direction"]),
    author: parseAuthor(r["sentBy"]),
    body: typeof r["body"] === "string" ? r["body"] : "",
    sentAt: str(r["sentAt"]),
    draft: metadata["pending_approval"] === true,
    automated: "automated" in metadata && metadata["automated"] !== false,
  };
}

function parseThread(
  raw: unknown,
  run: { id: string; status: string | null; startedAt: string | null; offerId: string | null },
): SellerThreadRow {
  const r = dict(raw);
  const profile = dict(r["profile"]);
  const dim = dict(profile["dim"]);
  const sellerId = str(r["sellerId"]) ?? "";
  const messages = (Array.isArray(r["messages"]) ? r["messages"] : []).map(parseMessage);
  const offers = (Array.isArray(r["offers"]) ? r["offers"] : []).map((o) =>
    parseOffer(o, run.offerId),
  );
  const approachedAt = str(r["createdAt"]);
  const firstReplyAt = str(r["firstReplyAt"]);
  // Time to offer measures the FIRST proposal; a renegotiated second one is not
  // a slower first response. The backend orders offers by `received_at`.
  const firstOffer = offers[0] ?? null;

  return {
    id: str(r["id"]) ?? `${run.id}-${sellerId}`,
    runId: run.id,
    runStatus: run.status,
    runStartedAt: run.startedAt,
    sellerId,
    displayName: str(profile["display_name"]) ?? sellerId,
    headline: str(profile["one_liner"]),
    profileUrl: str(r["profileUrl"]),
    // Backend-built and already environment-correct, so it is passed through
    // rather than assembled here. `str()` maps both a null and a backend that
    // predates the field to null, which is the same "draw no link" answer.
    fiverrOsChatUrl: str(r["fiverrOsChatUrl"]),
    avatarUrl: str(r["sellerAvatarUrl"]),
    state: str(r["state"]) ?? "",
    declineReason: str(r["declineReason"]),
    declineDetail: str(r["declineDetail"]),
    pickRationale: str(r["pickRationale"]),
    pickDomainRelevance: str(r["pickDomainRelevance"]),
    // "Search Tier" on the dashboard is the gateway's gig-quality grade.
    searchTier: str(dim["quality_bucket"]),
    country: str(dim["country"]),
    sellerLevel: str(dim["seller_level"]),
    subCategoryId: num(dim["primary_sub_category_id"]),
    rating: num(dim["rating"]),
    completedOrders: num(dim["completed_orders_count"]),
    isPro: bool(dim["is_pro"]),
    approachedAt,
    firstReplyAt,
    lastMessageAt: str(r["lastMessageAt"]),
    repliedInMinutes: minutesBetween(approachedAt, firstReplyAt),
    offerInMinutes: minutesBetween(approachedAt, firstOffer?.receivedAt ?? null),
    messageCount: messages.filter((m) => !m.draft).length,
    messages,
    offers,
    latestOffer: offers.length > 0 ? offers[offers.length - 1] : null,
    recommended: offers.some((o) => o.recommended),
    // `bool()` maps a missing field to null, which IS the right reading: a read
    // that resolved no pick set said nothing, so the row claims nothing.
    picked: bool(r["picked"]),
  };
}

/** Can this entry identify a thread at all?
 *
 *  Tolerance has a floor. Everything else here degrades a missing field to
 *  "unknown", but a row with neither an id nor a talent is not a degraded
 *  record — it is not a record, and parsing it anyway would emit rows whose ids
 *  COLLIDE (the id falls back to `${runId}-${sellerId}`, identical for every such
 *  entry), which React keys on. So these are dropped, not rendered blank. */
function identifiable(raw: unknown): boolean {
  const r = dict(raw);
  return str(r["id"]) !== null || str(r["sellerId"]) !== null;
}

/** Every talent approached for this project, newest run first (the order the
 *  backend returns runs in), talents in approach order within a run. */
export function parseSellerThreads(runs: readonly unknown[]): SellerThreadRow[] {
  const rows: SellerThreadRow[] = [];
  for (const raw of runs) {
    const r = dict(raw);
    const run = {
      id: str(r["runId"]) ?? "",
      status: str(r["status"]),
      startedAt: str(r["startedAt"]),
      offerId: str(r["recommendedOfferId"]),
    };
    for (const thread of Array.isArray(r["threads"]) ? r["threads"] : []) {
      if (!identifiable(thread)) continue;
      rows.push(parseThread(thread, run));
    }
  }
  return rows;
}

// --- the talent page: the same threads, keyed the other way ------------------

/** One thread on the TALENT page: the same record, carrying the project it
 *  belongs to instead of being nested under a run. `GET /api/admin/talents/{id}`
 *  returns these flat and newest-project-first. */
export interface TalentThreadRow extends SellerThreadRow {
  projectId: string | null;
  /** Raw `projects.name`, which is routinely the EMPTY string. Title it through
   *  `projectTitle()` — never render this directly (admin/CLAUDE.md). */
  projectName: string;
  projectSummary: string | null;
  projectStatus: string | null;
  ownerId: string | null;
  ownerEmail: string | null;
  ownerDisplayName: string | null;
}

/** Every project one talent was approached for. Tolerant in the same way as
 *  `parseSellerThreads`, and sharing its thread parser, so a talent's thread can
 *  never read differently on the two pages. */
export function parseTalentThreads(threads: readonly unknown[]): TalentThreadRow[] {
  const rows: TalentThreadRow[] = [];
  for (const raw of threads) {
    if (!identifiable(raw)) continue;
    const r = dict(raw);
    const project = dict(r["project"]);
    const owner = dict(project["owner"]);
    // The run fields ride on the thread itself here, rather than on a parent.
    const base = parseThread(raw, {
      id: str(r["runId"]) ?? "",
      status: str(r["runStatus"]),
      startedAt: str(r["runStartedAt"]),
      offerId: str(r["recommendedOfferId"]),
    });
    rows.push({
      ...base,
      projectId: str(project["id"]),
      projectName: typeof project["name"] === "string" ? project["name"] : "",
      projectSummary: str(project["summary"]),
      projectStatus: str(project["status"]),
      ownerId: str(owner["id"]),
      ownerEmail: str(owner["email"]),
      ownerDisplayName: str(owner["displayName"]),
    });
  }
  return rows;
}

// --- sorting -----------------------------------------------------------------

export type SellerSortKey =
  | "talent"
  | "searchTier"
  | "country"
  | "sellerLevel"
  | "subCategoryId"
  | "approachedAt"
  | "repliedInMinutes"
  | "offerInMinutes"
  | "messageCount"
  | "offerPrice"
  | "status";

/** Sort accessors, per lib/table-sort.ts: value EXTRACTORS, not field names, so
 *  a column can order by something other than what it prints. Status orders by
 *  its printed label; Offer orders by the price of the latest proposal. */
export const SELLER_SORT_ACCESSORS: Record<
  SellerSortKey,
  (row: SellerThreadRow) => string | number | boolean | null | undefined
> = {
  talent: (r) => r.displayName || r.sellerId,
  searchTier: (r) => r.searchTier,
  country: (r) => r.country,
  sellerLevel: (r) => r.sellerLevel,
  subCategoryId: (r) => r.subCategoryId,
  approachedAt: (r) => r.approachedAt,
  repliedInMinutes: (r) => r.repliedInMinutes,
  offerInMinutes: (r) => r.offerInMinutes,
  messageCount: (r) => r.messageCount,
  offerPrice: (r) => r.latestOffer?.priceCents ?? null,
  status: (r) => threadStatus(r.state, r.latestOffer).label,
};

export type TalentProjectSortKey =
  | "project"
  | "client"
  | "approachedAt"
  | "repliedInMinutes"
  | "offerInMinutes"
  | "messageCount"
  | "offerPrice"
  | "status";

/** The talent page's project table. `project` orders by the DERIVED title, not
 *  by `projects.name`: a large share of rows carry an empty name, and sorting on
 *  the raw column bunches every one of them at one end (admin/CLAUDE.md). */
export const TALENT_PROJECT_SORT_ACCESSORS: Record<
  TalentProjectSortKey,
  (row: TalentThreadRow) => string | number | boolean | null | undefined
> = {
  project: (r) =>
    projectTitle({ id: r.projectId ?? r.id, name: r.projectName, summary: r.projectSummary }),
  client: (r) => r.ownerDisplayName || r.ownerEmail,
  approachedAt: (r) => r.approachedAt,
  repliedInMinutes: (r) => r.repliedInMinutes,
  offerInMinutes: (r) => r.offerInMinutes,
  messageCount: (r) => r.messageCount,
  offerPrice: (r) => r.latestOffer?.priceCents ?? null,
  status: (r) => threadStatus(r.state, r.latestOffer).label,
};
